import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import classNames from 'classnames';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { coinIcons } from 'shared/constants';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useRoomStore } from 'shared/store/roomStore';
import { debounce } from 'shared/utils/debounce';

import boxEmpty from 'assets/png/box-empty.png';
import boxFull from 'assets/png/box-full.png';
import boxHalf from 'assets/png/box-half.png';

type CoinSymbol = keyof typeof coinIcons;

interface RoomBoxProps {
  amIWinner: boolean;
  animationsTriggered: boolean;
}

export const MobileRoomBox = ({ amIWinner, animationsTriggered }: RoomBoxProps) => {
  const [boxImage, setBoxImage] = useState(boxHalf);
  const [betText, setBetText] = useState('');
  const [fontSize, setFontSize] = useState(28);
  const { activeRoomId } = useParams();
  const { selectedCurrency } = useCurrenciesStore();

  const boxRef = useRef<HTMLImageElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const betTextRef = useRef<HTMLParagraphElement>(null);
  const { bet, playerB, roomStatus } = useRoomStore();

  // box image
  useEffect(() => {
    if (amIWinner) {
      setBoxImage(boxFull);
    } else {
      setBoxImage(boxEmpty);
    }
  }, [amIWinner]);
  useEffect(() => {
    if (bet) {
      let rawBet = bet;
      if (playerB.address !== NULL_ADDRESS) {
        rawBet = bet * 2;
      }
      const betString = rawBet.toString();
      const [, decimals] = betString.split('.');
      let formattedBet = '';
      const leadingZeros = decimals?.match(/^0+/)?.[0]?.length || 0;

      if (leadingZeros >= 3) {
        formattedBet = `0.0<span class="text-[16px]">${leadingZeros}</span>${
          decimals.slice(leadingZeros)[0]
        }`;
      } else {
        formattedBet = rawBet.toString();
      }
      if (rawBet >= 1_000_000) {
        formattedBet = `${(rawBet / 1_000_000).toFixed(1)}M`;
      } else if (rawBet >= 1_000) {
        formattedBet = `${(rawBet / 1_000).toFixed(1)}K`;
      }

      setBetText(`${formattedBet}`);
    }
  }, [bet, selectedCurrency, playerB]);

  useEffect(() => {
    const adjustFontSize = () => {
      if (textContainerRef.current && betTextRef.current) {
        const containerWidth = textContainerRef.current.offsetWidth;
        const textWidth = betTextRef.current.offsetWidth;

        if (textWidth > containerWidth) {
          const newFontSize = fontSize * (containerWidth / textWidth);
          setFontSize(newFontSize);
        }
      }
    };

    adjustFontSize();
    window.addEventListener('resize', adjustFontSize);

    return () => {
      window.removeEventListener('resize', adjustFontSize);
    };
  }, [betText, fontSize]);

  const getCoinIcon = (symbol: CoinSymbol | undefined) => {
    if (!symbol) return null;

    return <img src={coinIcons[symbol]} className="w-auto max-w-6 max-h-6 h-auto" />;
  };

  return (
    <div
      ref={boxRef}
      className={classNames(
        'absolute z-50 left-1/2 transform -translate-x-1/2 bottom-[130px] sm:bottom-[200px] z-1 min-w-[160px] w-[60%] blur-4 max-w-[350px]',
        animationsTriggered ? 'animate-slide-in-from-bottom' : 'animate-fast-appear',
      )}
    >
      <img src={boxImage} alt="box" />
      <div
        ref={textContainerRef}
        className="absolute z-50 text-white text-center top-[69%] left-[9%] w-[45%] h-[18%] font-bold  flex items-center justify-center whitespace-nowrap"
      >
        {betText && (
          <div
            className="flex gap-2 items-center"
            style={{ fontSize: `${fontSize}px` }}
            ref={betTextRef}
          >
            <p className="font-black" dangerouslySetInnerHTML={{ __html: betText }}></p>
            {getCoinIcon(selectedCurrency?.symbol as CoinSymbol | undefined)}
          </div>
        )}
      </div>
    </div>
  );
};
